import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import ParentPortalPageTemplate from "../../components/ParentPortalPageTemplate";
import ParentPortalDesktopShell from "./ParentPortalDesktop";
import ParentPortalMobileShell from "./components/ParentPortalMobileShell";
import { useCanEnterStaffPortal } from "../../authz/staffPortalAccess";
import { AuthContext } from "../../AuthProvider";
import { useActiveRole } from "../../utils/activeRole";
import { pickStartPagePreference, resolveStaffStartRoute } from "../../utils/startPagePreference";
import { useStartPageAccess } from "../../utils/useStartPageAccess";
import type { ParentActionsView } from "./components/ParentActionsSubnav";

interface ParentPortalResponsiveShellProps {
  tabItems: Array<{ key: string; label: string }>;
  mobileTabItems?: Array<{ key: string; label: string }>;
  activeTab: string;
  setActiveTab: (key: any) => void;
  summaryCards: Array<any>;
  familyName?: string;
  schoolName?: string;
  desktopTitle?: string;
  desktopChildren: React.ReactNode;
  mobileChildren: React.ReactNode;
  onOpenProfile?: () => void;
  onOpenSettings?: () => void;
  onOpenCalendarLink?: () => void;
  directorySearch?: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    loading?: boolean;
    placeholder?: string;
  };
  actionsView: ParentActionsView;
  setActionsView: (view: ParentActionsView) => void;
  openActionsCount: number;
  schoolTasksCount: number | null;
  showSchoolTasks: boolean;
  openActionsLabel: string;
  schoolTasksLabel: string;
}

export default function ParentPortalResponsiveShell({
  tabItems,
  mobileTabItems,
  activeTab,
  setActiveTab,
  summaryCards,
  familyName,
  schoolName,
  desktopTitle,
  desktopChildren,
  mobileChildren,
  onOpenProfile,
  onOpenSettings,
  onOpenCalendarLink,
  directorySearch,
  actionsView,
  setActionsView,
  openActionsCount,
  schoolTasksCount,
  showSchoolTasks,
  openActionsLabel,
  schoolTasksLabel,
}: ParentPortalResponsiveShellProps) {
  const canOpenTeacherPortal = useCanEnterStaffPortal();
  const navigate = useNavigate();
  const { keycloak } = useContext(AuthContext) as any;
  const { setActiveRole } = useActiveRole();
  const startPageAccess = useStartPageAccess();
  // Go straight to the staff start page. Going via "/" would send users whose start page is the
  // parent portal right back to the parent portal.
  const openTeacherPortal = () => {
    const route =
      resolveStaffStartRoute({
        ...startPageAccess,
        preference: pickStartPagePreference(null, keycloak?.tokenParsed),
      }) || "/dashboard";
    setActiveRole("admin");
    navigate(route, { replace: true });
  };
  const desktopShell = (
    <ParentPortalDesktopShell
      tabItems={tabItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      summaryCards={summaryCards}
      actionsView={actionsView}
      setActionsView={setActionsView}
      openActionsCount={openActionsCount}
      schoolTasksCount={schoolTasksCount}
      showSchoolTasks={showSchoolTasks}
      openActionsLabel={openActionsLabel}
      schoolTasksLabel={schoolTasksLabel}
    >
      {desktopChildren}
    </ParentPortalDesktopShell>
  );

  return (
    <>
      <div className="hidden lg:block">
        {desktopTitle ? (
          <ParentPortalPageTemplate title={desktopTitle}>{desktopShell}</ParentPortalPageTemplate>
        ) : (
          desktopShell
        )}
      </div>

      <ParentPortalMobileShell
        tabItems={mobileTabItems || tabItems}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        familyName={familyName}
        schoolName={schoolName}
        onOpenProfile={onOpenProfile}
        onOpenSettings={onOpenSettings}
        onOpenCalendarLink={onOpenCalendarLink}
        showTeacherPortal={canOpenTeacherPortal}
        onOpenTeacherPortal={openTeacherPortal}
        directorySearch={directorySearch}
        actionsView={actionsView}
        setActionsView={setActionsView}
        openActionsCount={openActionsCount}
        schoolTasksCount={schoolTasksCount}
        showSchoolTasks={showSchoolTasks}
        openActionsLabel={openActionsLabel}
        schoolTasksLabel={schoolTasksLabel}
      >
        {mobileChildren}
      </ParentPortalMobileShell>
    </>
  );
}
